/**
 * @file useNoticeAlerts.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-15
 * @modified 2026-10-15
 * @description The status bar's Alerts, live (Cam, 15 Oct 2026 — "Needs to
 *              read live from the db so that as soon as it is cleared fully
 *              the status bar alert and notices widget update instantly for
 *              all").
 *
 * One shared copy per tab of GET /api/notices/summary:
 *   { count, bySeverity, signature, ack: { ready, reason } }
 * count is this person's unacknowledged alerts (error and warning notices);
 * signature moves whenever an item appears or clears for anyone, or this
 * person acknowledges / undoes one.
 *
 *   useNoticeAlertsPoll({ enabled })   mounted ONCE (Dashboard) for an ADMIN /
 *                                      USER: polls every 20 s — and at once
 *                                      when the tab comes back into view (a
 *                                      hidden tab does not poll)
 *   useNoticeAlerts()                  read it anywhere (the status bar, the
 *                                      Notices widget, the alerts window)
 *   acknowledgeNotices(items) /        POST for the signed-in person; the
 *   unacknowledgeNotices(items)        answer's summary is published at once,
 *                                      so their count moves without a poll
 *
 * "Instant" for everyone else is therefore within one 20-second poll. When
 * the signature moves, every mounted notices list re-reads (useNotices).
 * Nothing is cached beyond this tab's last answer.
 */

import { useEffect, useState } from 'react';
import { API_URL } from '../config';

export const POLL_MS = 20000;

const EMPTY = Object.freeze({
  loaded: false,
  count: 0,
  bySeverity: { error: 0, warning: 0, info: 0 },
  signature: null,
  ack: { ready: true, reason: null }
});

let current = EMPTY;
const listeners = new Set();

/** Replace the shared copy and tell every mounted reader. */
export function publishNoticeSummary(summary) {
  if (!summary || typeof summary !== 'object') return;
  current = {
    loaded: true,
    count: Number(summary.count) || 0,
    bySeverity: summary.bySeverity || EMPTY.bySeverity,
    signature: summary.signature || null,
    ack: summary.ack || EMPTY.ack
  };
  listeners.forEach(fn => fn(current));
}

/** The shared copy as it is now (tests, and code outside React). */
export function noticeSummaryNow() {
  return current;
}

/** For tests: back to nothing loaded. */
export function resetNoticeSummary() {
  current = EMPTY;
  listeners.forEach(fn => fn(current));
}

export function useNoticeAlerts() {
  const [state, setState] = useState(current);
  useEffect(() => {
    let live = true;
    const onChange = (v) => { if (live) setState(v); };
    listeners.add(onChange);
    if (state !== current) setState(current);
    return () => { live = false; listeners.delete(onChange); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return state;
}

export async function fetchNoticeSummary() {
  const res = await fetch(`${API_URL}/api/notices/summary`, { credentials: 'include', cache: 'no-store' });
  if (!res.ok) return null;
  const body = await res.json();
  publishNoticeSummary(body);
  return body;
}

export function useNoticeAlertsPoll({ enabled = true } = {}) {
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        // A failed poll keeps the last answer: a blip in our server must not
        // clear (or invent) anyone's alerts.
        try { await fetchNoticeSummary(); } catch (_) { /* keep the last one */ }
      }
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };
    tick();
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(timer);
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);
}

async function post(path, items) {
  const res = await fetch(`${API_URL}/api/notices/${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items })
  });
  let body = null;
  try { body = await res.json(); } catch (_) { body = null; }
  if (!res.ok) {
    const err = new Error(body?.error || `Could not save that (HTTP ${res.status})`);
    err.code = body?.code || null;
    throw err;
  }
  if (body?.summary) publishNoticeSummary(body.summary);
  return body;
}

/** Acknowledge for the signed-in person. items: [{ notice, key }]. */
export const acknowledgeNotices = (items) => post('acknowledge', items);

/** Take the signed-in person's acknowledgements back. */
export const unacknowledgeNotices = (items) => post('unacknowledge', items);
